import api from '@/features/api/axios';
import { logger } from '@/features/help/utils/logger';

const DEFAULT_FAVICON_PATH = '/favicon.ico';

// Purpose: Ensure favicon links exist and return all icon-related entries.
const getOrCreateFaviconLinks = (): HTMLLinkElement[] => {
  const existing = Array.from(
    document.querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="shortcut icon"]')
  );

  if (existing.length > 0) {
    return existing;
  }

  const iconLink = document.createElement('link');
  iconLink.rel = 'icon';
  document.head.appendChild(iconLink);

  const shortcutIconLink = document.createElement('link');
  shortcutIconLink.rel = 'shortcut icon';
  document.head.appendChild(shortcutIconLink);

  return [iconLink, shortcutIconLink];
};

// Purpose: Apply a favicon URL to the document.
const applyFaviconUrl = (faviconUrl: string) => {
  const links = getOrCreateFaviconLinks();
  links.forEach((link) => {
    link.href = faviconUrl;
  });
};

// Purpose: Fetch the tenant favicon URL from the backend.
const fetchTenantFaviconUrl = async (params: {
  domain: string;
  userShortId?: string;
}): Promise<string | null> => {
  try {
    const response = await api.get('/api/logo/favicon', {
      params: {
        domain: params.domain,
        userShortId: params.userShortId,
      },
    });

    return response?.data?.faviconUrl ?? null;
  } catch (error) {
    logger.warn('Favicon', 'Failed to fetch tenant favicon', { error, params });
    return null;
  }
};

// Purpose: Set favicon based on tenant logo with fallback to default.
export const setTenantFavicon = async (params: {
  domain: string;
  userShortId?: string;
}): Promise<void> => {
  const faviconUrl = await fetchTenantFaviconUrl(params);

  if (faviconUrl) {
    applyFaviconUrl(faviconUrl);
    return;
  }

  applyFaviconUrl(DEFAULT_FAVICON_PATH);
};
